/**
 * NowLineHeader Component
 * =======================
 * 
 * Implements the "Now-Line" concept from DESIGN_SPEC_CASE_PIPELINE_V2.md
 * Shows two summary cards at the top of any dashboard:
 * - LEFT: Needs Your Attention (you are the bottleneck)
 * - RIGHT: No Action Needed (AI handling / waiting on others)
 * 
 * This helps staff instantly know which cases need THEM vs. which are being handled.
 */

import React from 'react';
import { AlertTriangle, CheckCircle, Clock, ArrowRight } from 'lucide-react';

export interface NowLineStats {
  needsAttention: {
    count: number;
    breakdown: {
      label: string;
      count: number;
    }[];
  };
  noActionNeeded: {
    count: number;
    breakdown: {
      label: string;
      count: number;
    }[];
  };
}

interface NowLineHeaderProps {
  stats: NowLineStats;
  userName?: string;
  onViewNeedsAttention?: () => void;
  onViewNoAction?: () => void;
  loading?: boolean;
}

export default function NowLineHeader({
  stats,
  userName,
  onViewNeedsAttention,
  onViewNoAction,
  loading = false
}: NowLineHeaderProps) {
  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="h-8 w-48 bg-[var(--color-bg-elevated)] rounded animate-pulse"></div>
          <div className="h-5 w-32 bg-[var(--color-bg-elevated)] rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-24 bg-[var(--color-bg-elevated)] rounded-lg animate-pulse"></div>
          <div className="h-24 bg-[var(--color-bg-elevated)] rounded-lg animate-pulse"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {/* Greeting Row */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold font-serif text-[var(--color-text-primary)]">
          {greeting()}{userName ? `, ${userName}` : ''}
        </h1>
        <span className="text-sm font-mono text-[var(--color-text-muted)]">{formatDate()}</span>
      </div>

      {/* Now-Line Split Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT: Needs Your Attention */}
        <button
          onClick={onViewNeedsAttention}
          className="group bg-[var(--color-bg-surface)] border-2 border-[var(--color-border)] hover:border-[var(--color-brand)] rounded-xl p-5 text-left transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--color-brand)] focus:ring-offset-2"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-[rgba(255,102,0,0.12)] rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-[var(--color-brand)]" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[var(--color-needs-human-text)] uppercase tracking-wide">
                  Needs Your Attention
                </h3>
                <p className="text-3xl font-bold font-mono text-[var(--color-needs-human-text)] mt-1">
                  {stats.needsAttention.count} {stats.needsAttention.count === 1 ? 'case' : 'cases'}
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[var(--color-needs-human-text)] group-hover:text-[var(--color-brand)] group-hover:translate-x-1 transition-all" />
          </div>

          {/* Breakdown */}
          {stats.needsAttention.breakdown.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
              <div className="flex flex-wrap gap-2">
                {stats.needsAttention.breakdown.map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-mono bg-[rgba(255,102,0,0.12)] text-[var(--color-needs-human-text)]"
                  >
                    {item.count} {item.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </button>

        {/* RIGHT: No Action Needed */}
        <button
          onClick={onViewNoAction}
          className="group bg-[var(--color-bg-surface)] border-2 border-[var(--color-border)] hover:border-[var(--color-verified)] rounded-xl p-5 text-left transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--color-verified)] focus:ring-offset-2"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-[rgba(34,197,94,0.12)] rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[var(--color-verified)]" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[var(--color-verified)] uppercase tracking-wide">
                  No Action Needed
                </h3>
                <p className="text-3xl font-bold font-mono text-[var(--color-verified)] mt-1">
                  {stats.noActionNeeded.count} {stats.noActionNeeded.count === 1 ? 'case' : 'cases'}
                </p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-[var(--color-verified)] group-hover:text-[var(--color-verified)] group-hover:translate-x-1 transition-all" />
          </div>

          {/* Breakdown */}
          {stats.noActionNeeded.breakdown.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
              <div className="flex flex-wrap gap-2">
                {stats.noActionNeeded.breakdown.map((item, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium font-mono bg-[rgba(34,197,94,0.12)] text-[var(--color-verified)]"
                  >
                    {item.count} {item.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
